import React from 'react';
import { Icon, Card, Flex, Image, Text } from '@opendoor/bricks/core';
import { shadows } from '@opendoor/bricks/theme/eero';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { useObservability } from 'shared/helpers/observability';

interface ProxyImageCardProps {
  analyticsName: string;
  imageUrl: string;
  title: string;
  description: string;
  /** Checks if any image has been selected since rendering the next step in wizard */
  hasAnyImageBeenSelected: boolean;
  /** Checks to see if the image/component has been selected */
  selected?: boolean;
  onClick: (e: React.TouchEvent | React.MouseEvent | null) => void;
}
/**
 * Renders proxy image card
 * component-test-check-ignore
 */
export const ProxyImageCard: React.FC<ProxyImageCardProps> = ({
  analyticsName,
  imageUrl,
  title,
  description,
  hasAnyImageBeenSelected,
  selected,
  onClick,
}) => {
  const { isMobile } = useDevice();
  const { trackEvent } = useObservability();

  return (
    <Card
      as="button"
      variant="secondary"
      padding="0"
      opacity={isMobile && hasAnyImageBeenSelected && !selected ? 0.5 : 1}
      overflow="hidden"
      width="202px"
      transition="0.25s ease"
      height="100%"
      minHeight="274px"
      boxSizing="content-box"
      outline="2px solid transparent"
      boxShadow={selected ? 'z2' : undefined}
      _hover={{ boxShadow: shadows.z2, cursor: 'pointer' }}
      _focus={{ outline: '2px solid black', boxShadow: 'z2' }}
      _focusVisible={{ outline: '2px solid black' }}
      onClick={(e) => {
        onClick(e);
        trackEvent('click', analyticsName);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onClick(null);
      }}
      aria-label={`${analyticsName} ${title}`}
      data-testid={analyticsName}
    >
      <Flex flexDir="column" height="100%" width="100%">
        <Image objectFit="cover" src={imageUrl} width="100%" height="133px" alt={title} />
        <Flex flexDir="column" alignItems="start" p="5">
          <Flex flexDir="row" justifyContent="space-between" alignItems="center" w="100%">
            <Text fontSize="s2" fontWeight="semibold" lineHeight="s2">
              {title}
            </Text>
            {selected && <Checkmark />}
          </Flex>
          <Text fontSize="s1" textAlign="start">
            {description}
          </Text>
        </Flex>
      </Flex>
    </Card>
  );
};

const Checkmark = () => (
  <Flex
    backgroundColor="brand50"
    aria-label="Selected"
    borderRadius="rounded"
    w="20px"
    h="20px"
    alignItems="center"
    justifyContent="center"
  >
    <Icon name="checkmark" size={12} color="neutrals0" aria-hidden />
  </Flex>
);
